/**
 * PriorityRow Component
 *
 * Displays a single priority level with icon, label, total count,
 * status distribution bar, and detailed status breakdown.
 *
 * @module pages/DashboardPage/components/PriorityRow
 */

import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  IconUrgent,
  IconAlertCircle,
  IconInfoCircle,
  IconMinus,
} from '@tabler/icons-react';

import { ICON_SIZE_LG, ICON_SIZE_SM } from '@/constants/ui';

import { StatusDistributionBar } from '../StatusDistributionBar';
import type { PriorityLevel, PriorityRowProps } from './PriorityRow.types';
import styles from './PriorityRow.module.css';

/** Status keys displayed in the stats row */
const STAT_KEYS = ['pending', 'active', 'completed', 'delayed'] as const;

/** Configuration for each priority level */
const PRIORITY_CONFIG: Record<
  PriorityLevel,
  {
    Icon: typeof IconUrgent;
    color: string;
    /** Override dark-mode background token when default `${color}-9` blends with card bg */
    darkBgColor?: string;
    labelKey: string;
  }
> = {
  urgent: {
    Icon: IconUrgent,
    color: 'red',
    labelKey: 'dashboard.priorities.urgent',
  },
  high: {
    Icon: IconAlertCircle,
    color: 'orange',
    labelKey: 'dashboard.priorities.high',
  },
  medium: {
    Icon: IconInfoCircle,
    color: 'yellow',
    labelKey: 'dashboard.priorities.medium',
  },
  low: {
    Icon: IconMinus,
    color: 'gray',
    darkBgColor: 'dark-5',
    labelKey: 'dashboard.priorities.low',
  },
};

/**
 * PriorityRow renders a row for a single priority level
 * with icon box, distribution bar, and stats breakdown.
 */
export const PriorityRow = memo(function PriorityRow({
  data,
  totalTasks,
  compact = false,
}: PriorityRowProps) {
  const { t } = useTranslation();
  const config = PRIORITY_CONFIG[data.priority];
  const { Icon } = config;

  const colorStyle = useMemo(
    () =>
      ({
        '--priority-color': `var(--mantine-color-${config.color}-6)`,
        '--priority-color-light': `var(--mantine-color-${config.color}-1)`,
        '--priority-color-icon-dark': `var(--mantine-color-${config.color}-4)`,
        '--priority-color-bg-dark': `var(--mantine-color-${config.darkBgColor ?? `${config.color}-9`})`,
      }) as CSSProperties,
    [config.color, config.darkBgColor]
  );

  const percentage =
    totalTasks > 0 ? Math.round((data.total / totalTasks) * 100) : 0;

  return (
    <div
      className={clsx(styles.row, compact && styles.compact)}
      style={colorStyle}
    >
      <div className={styles.iconBox}>
        <Icon size={compact ? ICON_SIZE_SM : ICON_SIZE_LG} />
      </div>
      <div className={styles.infoBlock}>
        <div className={styles.headerRow}>
          <span className={styles.priorityLabel}>{t(config.labelKey)}</span>
          {compact ? (
            <span className={styles.compactRight}>
              <span className={styles.totalValue}>{data.total}</span>
              <span className={styles.percentBadge}>
                {t('dashboard.priorities.stats.percent', {
                  percent: percentage,
                })}
              </span>
            </span>
          ) : (
            <span className={styles.totalValue}>{data.total}</span>
          )}
        </div>
        {!compact && (
          <>
            <StatusDistributionBar
              pending={data.pending}
              active={data.active}
              completed={data.completed}
              delayed={data.delayed}
              total={data.total}
            />
            <div className={styles.statsRow}>
              {STAT_KEYS.map((statKey) => (
                <span key={statKey} className={styles.statItem}>
                  <span className={styles.statLabel}>
                    {t(`dashboard.priorities.stats.${statKey}`)}
                  </span>
                  <span className={styles.statValue}>{data[statKey]}</span>
                </span>
              ))}
              <span className={clsx(styles.statItem, styles.percentStat)}>
                <span className={styles.statValue}>
                  {t('dashboard.priorities.stats.percent', {
                    percent: percentage,
                  })}
                </span>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

PriorityRow.displayName = 'PriorityRow';
